import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, getDocs, doc, setDoc, deleteDoc, addDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ReactMarkdown from 'react-markdown';

function AdminExperiences() {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [editingExperience, setEditingExperience] = useState(null);
  const [newExperience, setNewExperience] = useState({
    title: '',
    description: '',
    longDescription: '',
    videoUrl: '',
    imageUrl: '',
    mediaType: 'video', // 'video' or 'image'
    slug: '',
  });
  const [newImageFile, setNewImageFile] = useState(null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'experiences'));
      const experiencesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setExperiences(experiencesList);
    } catch (error) {
      console.error('Error fetching experiences:', error);
      setFeedback({ message: `Error al cargar experiencias: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === 'image') {
        setNewImageFile(file);
      } else if (fileType === 'video') {
        setNewVideoFile(file);
      }
    }
  };

  const handleAddExperience = async (e) => {
    e.preventDefault();
    setFeedback({ message: '', type: '' });

    const { title, description, slug, mediaType } = newExperience;

    if (!title || !description || !slug || (mediaType === 'video' && !newVideoFile) || (mediaType === 'image' && !newImageFile)) {
      setFeedback({ message: 'Por favor, completa todos los campos requeridos y selecciona un archivo.', type: 'error' });
      return;
    }

    try {
      let imageUrl = '';
      let videoUrl = '';

      if (mediaType === 'image' && newImageFile) {
        const imageRef = ref(storage, `experiences/${newImageFile.name}`);
        await uploadBytes(imageRef, newImageFile);
        imageUrl = await getDownloadURL(imageRef);
      } else if (mediaType === 'video' && newVideoFile) {
        const videoRef = ref(storage, `experiences/${newVideoFile.name}`);
        await uploadBytes(videoRef, newVideoFile);
        videoUrl = await getDownloadURL(videoRef);
      }

      await addDoc(collection(db, 'experiences'), {
        ...newExperience,
        imageUrl,
        videoUrl,
        createdAt: new Date(),
      });
      setFeedback({ message: 'Experiencia agregada con éxito!', type: 'success' });
      setNewExperience({ title: '', description: '', longDescription: '', videoUrl: '', imageUrl: '', mediaType: 'video', slug: '' });
      setNewImageFile(null);
      setNewVideoFile(null);
      setShowForm(false);
      fetchExperiences();
    } catch (error) {
      console.error('Error adding experience:', error);
      setFeedback({ message: `Error al agregar experiencia: ${error.message}`, type: 'error' });
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setFeedback({ message: '', type: '' });
    if (!editingExperience || !editingExperience.id) return;

    const { title, description, slug, mediaType } = editingExperience;

    if (!title || !description || !slug) {
      setFeedback({ message: 'Por favor, completa todos los campos requeridos.', type: 'error' });
      return;
    }

    try {
      const experienceRef = doc(db, 'experiences', editingExperience.id);
      const { id, ...dataToUpdate } = editingExperience; // Exclude id from data

      if (mediaType === 'image' && newImageFile) {
        const imageRef = ref(storage, `experiences/${newImageFile.name}`);
        await uploadBytes(imageRef, newImageFile);
        dataToUpdate.imageUrl = await getDownloadURL(imageRef);
        dataToUpdate.videoUrl = ''; // Clear video URL if switching to image
      } else if (mediaType === 'video' && newVideoFile) {
        const videoRef = ref(storage, `experiences/${newVideoFile.name}`);
        await uploadBytes(videoRef, newVideoFile);
        dataToUpdate.videoUrl = await getDownloadURL(videoRef);
        dataToUpdate.imageUrl = ''; // Clear image URL if switching to video
      }
      
      await updateDoc(experienceRef, {
        ...dataToUpdate,
        updatedAt: new Date(),
      });

      setFeedback({ message: 'Experiencia actualizada con éxito!', type: 'success' });
      setEditingExperience(null);
      setNewImageFile(null);
      setNewVideoFile(null);
      setShowForm(false);
      fetchExperiences();
    } catch (error) {
      console.error('Error updating experience:', error);
      setFeedback({ message: `Error al actualizar experiencia: ${error.message}`, type: 'error' });
    }
  };

  const handleEditClick = (experience) => {
    const url = experience.imageUrl || experience.videoUrl;
    const isImage = experience.mediaType === 'image' || (!experience.mediaType && url && /\.(jpg|jpeg|png|gif)$/i.test(url));
    
    const mediaType = isImage ? 'image' : 'video';
    const imageUrl = isImage ? url : '';
    const videoUrl = isImage ? '' : url;

    setEditingExperience({ 
      ...experience, 
      mediaType,
      imageUrl,
      videoUrl
    });
    setShowForm(true);
  };

  const handleDeleteExperience = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta experiencia?')) {
      setFeedback({ message: '', type: '' });
      try {
        await deleteDoc(doc(db, 'experiences', id));
        setFeedback({ message: 'Experiencia eliminada con éxito!', type: 'success' });
        fetchExperiences();
      } catch (error) {
        console.error('Error deleting experience:', error);
        setFeedback({ message: `Error al eliminar experiencia: ${error.message}`, type: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const isEditing = !!editingExperience;
    const setState = isEditing ? setEditingExperience : setNewExperience;

    setState(prev => {
      const newState = { ...prev, [name]: value };

      // When mediaType is changed, try to be smart about the URL
      if (name === 'mediaType') {
        const url = newState.videoUrl || newState.imageUrl;
        const isImage = url && /\.(jpg|jpeg|png|gif)$/i.test(url);

        if (value === 'image' && isImage && !newState.imageUrl) {
          newState.imageUrl = url;
          newState.videoUrl = '';
        } else if (value === 'video' && !isImage && !newState.videoUrl) {
          newState.videoUrl = url;
          newState.imageUrl = '';
        }
      }
      return newState;
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h2>Administrar Experiencias</h2>

      {feedback.message && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '5px',
          backgroundColor: feedback.type === 'success' ? '#d4edda' : '#f8d7da',
          color: feedback.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${feedback.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {feedback.message}
        </div>
      )}

      {showForm ? (
        <div style={{ marginBottom: '40px', border: '1px solid #ddd', padding: '20px', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
          <h3>{editingExperience ? 'Editar Experiencia' : 'Agregar Nueva Experiencia'}</h3>
          <form onSubmit={editingExperience ? handleSaveEdit : handleAddExperience}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Título:</label>
              <input
                type="text"
                name="title"
                value={editingExperience ? editingExperience.title : newExperience.title}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                required
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Descripción:</label>
              <textarea
                name="description"
                value={editingExperience ? editingExperience.description : newExperience.description}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '80px' }}
                required
              ></textarea>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Descripción Larga:</label>
              <textarea
                name="longDescription"
                value={editingExperience ? editingExperience.longDescription : newExperience.longDescription}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '120px' }}
              ></textarea>
            </div>
            {/* Markdown Preview */}
            {(editingExperience ? editingExperience.longDescription : newExperience.longDescription) && (
              <div style={{ marginBottom: '15px', padding: '10px', border: '1px dashed #ccc', borderRadius: '4px', backgroundColor: '#f0f0f0' }}>
                <h4 style={{ marginTop: '0', marginBottom: '10px' }}>Vista Previa Markdown:</h4>
                <ReactMarkdown>{editingExperience ? editingExperience.longDescription : newExperience.longDescription}</ReactMarkdown>
              </div>
            )}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Slug (URL amigable):</label>
              <input
                type="text"
                name="slug"
                value={editingExperience ? editingExperience.slug : newExperience.slug}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                required
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Tipo de Media:</label>
              <select
                name="mediaType"
                value={editingExperience ? editingExperience.mediaType : newExperience.mediaType}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                <option value="video">Video</option>
                <option value="image">Imagen</option>
              </select>
            </div>

            {(editingExperience ? editingExperience.mediaType : newExperience.mediaType) === 'video' ? (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Archivo de Video:</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleFileChange(e, 'video')}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Archivo de Imagen:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'image')}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {editingExperience ? 'Guardar Cambios' : 'Agregar Experiencia'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      ) : (
        <button
          onClick={() => {
            setEditingExperience(null);
            setNewExperience({ title: '', description: '', longDescription: '', videoUrl: '', imageUrl: '', mediaType: 'video', slug: '' });
            setShowForm(true);
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            marginBottom: '20px'
          }}
        >
          Agregar Nueva Experiencia
        </button>
      )}

      {/* Lista de Experiencias */}
      <h3>Experiencias Existentes</h3>
      {loading ? (
        <p>Cargando experiencias...</p>
      ) : (
        <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {experiences.length === 0 ? (
            <p>No hay experiencias añadidas aún.</p>
          ) : (
            experiences.map(experience => (
              <div key={experience.id} style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', backgroundColor: 'white' }}>
                {(() => {
                  const url = experience.imageUrl || experience.videoUrl;
                  const isImage = experience.mediaType === 'image' || (!experience.mediaType && url && /\.(jpg|jpeg|png|gif)$/i.test(url));

                  if (isImage) {
                    return <img src={url} alt={experience.title} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />;
                  } else if (url) {
                    return <video src={url} controls style={{ width: '100%', height: '180px', objectFit: 'cover', backgroundColor: '#000' }} />;
                  } else {
                    return (
                      <div style={{ width: '100%', height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#999' }}>No media</span>
                      </div>
                    );
                  }
                })()}
                <div style={{ padding: '15px' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{experience.title}</h4>
                  <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>{experience.description.substring(0, 100)}...</p>
                  <p style={{ fontSize: '0.8em', color: '#888' }}>Slug: {experience.slug}</p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <button
                      onClick={() => handleEditClick(experience)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em'
                      }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteExperience(experience.id)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em'
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default AdminExperiences;